import { CompositeFilterDescriptor, isCompositeFilterDescriptor } from '@progress/kendo-data-query';
import { Chip, ChipList, ChipListDataChangeEvent, ChipProps } from '@progress/kendo-react-buttons';
import { differenceBy } from 'lodash';
import { FC, useCallback, useMemo } from 'react';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import { IFilters } from 'src/types/PostBody';
import { filterOperatorsValue } from 'src/types/filter';
import { modifyFilters } from 'src/utils/requestInterceptor';

interface ChipListData {
  field: string;
  title: string;
}

interface Props {
  periods: number;
}

const YearFilterPills: FC<Props> = ({ periods }) => {
  const chipListData = useMemo(
    () => [
      { field: 'code', title: 'GL Code', dataType: 'String' },
      { field: 'description', title: 'Description', dataType: 'String' },
      { field: 'isFixed', title: 'Fixed Code', dataType: 'Boolean' },
      { field: 'isActive', title: 'Active Code', dataType: 'Boolean' },
      ...[...new Array(periods)].map((_, i) => ({
        field: `budgetPeriod${String(i + 1).padStart(2, '0')}`,
        title: `Period ${i + 1} Forecast Amt`,

        dataType: 'Number',
      })),
      ...[...new Array(periods)].map((_, i) => ({
        field: `initialBudgetPeriod${String(i + 1).padStart(2, '0')}`,
        title: `Period ${i + 1} Budget Amt`,
        dataType: 'Number',
      })),
    ],
    [periods]
  );

  const { yearCheckbookDataState, setYearCheckbookDataState } = useViewCheckbook();
  const filterData: IFilters[] = modifyFilters(yearCheckbookDataState.filter);

  const pillsData = useMemo(() => {
    let data: ChipListData[] = [];
    filterData.forEach((f) => {
      chipListData.forEach((c) => {
        if (f.field === c.field) {
          const filterOperator = filterOperatorsValue.find(
            (operator) => operator.operator === f.operator
          );
          const fieldValue =
            c.dataType === 'String'
              ? `"${f.value ?? ''}"`
              : c.dataType === 'Boolean'
              ? f.value === 'true'
                ? 'Yes'
                : 'No'
              : f.value ?? '';
          const title = `${c.title} ${filterOperator?.value} ${fieldValue} `;
          data.push({ ...c, title });
        }
        return;
      });
    });
    return data;
  }, [chipListData, filterData]);

  const filterGroupByField = (field: string, filter: CompositeFilterDescriptor | undefined) => {
    const compositeFilters = filter?.filters.filter((f) => {
      if (isCompositeFilterDescriptor(f)) {
        return (
          f.filters &&
          f.filters.length &&
          f.filters.length <= 2 &&
          f.filters.find((nf) => isCompositeFilterDescriptor(nf) || nf.field !== field)
        );
      }
      return false;
    });
    return compositeFilters ?? [];
  };

  const handleDataChange = useCallback(
    (event: ChipListDataChangeEvent) => {
      const value = differenceBy(pillsData, event.value, 'field')[0];
      const filters = filterGroupByField(value.field, yearCheckbookDataState.filter);
      setYearCheckbookDataState((prev) => ({ ...prev, filter: { logic: 'and', filters } }));
    },
    [pillsData, setYearCheckbookDataState, yearCheckbookDataState.filter]
  );

  return (
    <div className="mb-2">
      <ChipList
        data={pillsData}
        selection="single"
        textField="title"
        valueField="field"
        onDataChange={handleDataChange}
        size={'small'}
        chip={(props: ChipProps) => <Chip removable={true} {...props} />}
      />
    </div>
  );
};

export default YearFilterPills;
